import { ClearFiltersButton } from "@/components/ui/ClearFiltersButton";
import { EmailWhatsappText } from "@/components/ui/EmailWhatsappText";
import { ErrorResult } from "@/components/ui/ErrorResult";
import {
  ModalCUClient,
  ModalCUClientRef,
} from "@/components/ui/modals/client/ModalCUClient";
import {
  ModalVClientShopping,
  ModalVClientShoppingRef,
} from "@/components/ui/modals/client/ModalVClientShopping";
import { SectionPrivateHeader } from "@/components/ui/SectionPrivateHeader";
import useColumnSearch from "@/hooks/table/useColumnSearch";
import useTableFilters from "@/hooks/table/useTableFilters";
import { useDeleteClient } from "@/services/clients/mutation";
import { useClients } from "@/services/clients/queries";
import { Client } from "@/types/client";
import {
  DeleteOutlined,
  EditOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { message, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useAuth } from "@/auth/AuthProvider";
import { useRef } from "react";

export const ClientsPrivate = () => {
  const { user: currentUser } = useAuth();
  const isReadOnly = currentUser?.role === "auditor";
  const [messageApi, contextHolder] = message.useMessage();

  const { tableParams, tableKey, resetFilters, handleTableChange } =
    useTableFilters();

  const { data, isLoading, isError, error, isFetching, refetch } =
    useClients(tableParams);

  const modalCURef = useRef<ModalCUClientRef>(null);
  const modalVCSRef = useRef<ModalVClientShoppingRef>(null);

  const { getColumnSearchProps } = useColumnSearch<Client>();

  const columns: ColumnsType<Client> = [
    {
      title: "Tipo cliente",
      dataIndex: "type",
      key: "type",
      render: (text: Client["type"]) => {
        const type_map = {
          person: {
            color: "green",
            label: "Persona",
          },
          company: {
            color: "blue",
            label: "Empresa",
          },
        };

        const type = type_map[text];

        if (!type) return null;

        return <Tag color={type.color}>{type.label}</Tag>;
      },
      filters: [
        {
          text: "Empresa",
          value: "company",
        },
        {
          text: "Persona",
          value: "person",
        },
      ],
    },
    {
      title: "RUN",
      dataIndex: "tax_id",
      key: "tax_id",
      ...getColumnSearchProps("tax_id"),
      render: (text: string) => (text ? text : <Tag color="red">Sin RUN</Tag>),
    },
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      ...getColumnSearchProps("name"),
    },
    {
      title: "Correo electrónico",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
      render: (text: string) =>
        text ? (
          <EmailWhatsappText value={text} type="email" />
        ) : (
          <Tag color="red">Sin correo</Tag>
        ),
    },
    {
      title: "Teléfono",
      dataIndex: "phone",
      key: "phone",
      render: (text: string) =>
        text ? (
          <EmailWhatsappText value={text} type="whatsapp" />
        ) : (
          <Tag color="red">Sin teléfono</Tag>
        ),
    },
    {
      title: "Descuento",
      dataIndex: "discount_percent",
      key: "discount_percent",
      render: (value: number) => (value ? `${value}%` : "0%"),
    },
    {
      title: "¿Crédito?",
      dataIndex: "credit_enabled",
      key: "credit_enabled",
      render: (value: boolean) => (
        <Tag color={value ? "green" : "default"}>{value ? "Sí" : "No"}</Tag>
      ),
      filters: [
        {
          text: "Sí",
          value: 1,
        },
        {
          text: "No",
          value: false,
        },
      ],
    },
    {
      title: "Crédito Usado",
      dataIndex: "credit_balance",
      key: "credit_balance",
      render: (value: number) => {
        if (!value) return "$0";
        return `$${value.toLocaleString("es-CL")}`;
      },
    },
    {
      title: "Límite Crédito",
      dataIndex: "credit_limit",
      key: "credit_limit",
      render: (value: number) => {
        if (!value) return "$0";
        return `$${value.toLocaleString("es-CL")}`;
      },
    },

    {
      title: "Acciones",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (id: Client["id"], record: Client) => (
        <Space size="middle">
          {!isReadOnly && (
            <Tooltip placement="top" className="cursor-pointer" title="Editar">
              <button
                onClick={() => {
                  modalCURef.current?.childFunction(record);
                }}
              >
                <EditOutlined className="text-lg" />
              </button>
            </Tooltip>
          )}

          {/* VIEW */}
          <Tooltip
            placement="top"
            className="cursor-pointer"
            title="Ver compras de cliente"
          >
            <button
              onClick={() => {
                modalVCSRef.current?.childFunction(record);
              }}
            >
              <ShoppingOutlined className="text-lg" />
            </button>
          </Tooltip>

          {!isReadOnly && (
            <Tooltip placement="top" className="cursor-pointer" title="Eliminar">
              <div>
                <Popconfirm
                  title="¿Estás seguro/a que deseas desactivar a este cliente?"
                  okText="Sí, Eliminar"
                  cancelText="No, cancelar"
                  cancelButtonProps={{
                    style: { borderRadius: 12 },
                  }}
                  okButtonProps={{
                    style: {
                      backgroundColor: "#ef4444",
                      border: 0,
                    },
                  }}
                  placement="left"
                  onConfirm={() => {
                    onDeleteClient(id);
                  }}
                >
                  <button className="cursor-pointer">
                    <DeleteOutlined className="text-lg" />
                  </button>
                </Popconfirm>
              </div>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const deleteClient = useDeleteClient();

  const onDeleteClient = async (id: Client["id"]) => {
    deleteClient.mutate(id, {
      onSuccess: () => {
        messageApi.success(`El cliente se ha eliminado correctamente.`);
      },
      onError: () => {
        messageApi.error("Ups, algo salió mal. Intenta nuevamente.");
      },
    });
  };

  if (isError) {
    return <ErrorResult error={error} refetch={refetch} />;
  }

  return (
    <>
      {contextHolder}
      <SectionPrivateHeader
        title="Clientes"
        existsButton={!isReadOnly}
        onButtonClick={isReadOnly ? undefined : () => modalCURef.current?.childFunction()}
      />

      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-3">
        <div className="ml-auto max-w-fit">
          <span className="text-xs px-2 py-1 rounded-full bg-[#070B0E] text-gray-300 border border-[#1A2328]">
            {data?.total ?? 0} clientes
          </span>
        </div>

        <ClearFiltersButton onClear={resetFilters} />
      </div>

      <Table<Client>
        columns={columns}
        dataSource={data?.items}
        key={tableKey}
        pagination={{
          current: tableParams?.pagination.current,
          pageSize: tableParams?.pagination.pageSize,
        }}
        onChange={handleTableChange}
        loading={isLoading || isFetching || deleteClient.isPending}
        scroll={{ x: 1000 }}
      />

      <ModalCUClient ref={modalCURef} />
      <ModalVClientShopping ref={modalVCSRef} />
    </>
  );
};
